import React, { useEffect, useState } from "react";
import moment from "moment";
import "moment-timezone";
import { useParams } from "react-router-dom";
import { Box, Button, Container, Grid, Link, Typography } from "@mui/material";
import {
  delay,
  getData,
  postData,
  titleCase
} from "../../components/utilities/UtilityFunctions";
import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingMessage from "../../components/modals/ModalLoadingMessage";
import { globalVariables } from "../../global/GlobalVariables";
import { useConfirm } from "material-ui-confirm";
import { refreshTokens } from "../../components/user/BCPAuth";

export default function SubscriptionComplete(props) {
  let { subscriptionId } = useParams();

  if (!subscriptionId) {
    subscriptionId = props?.subscriptionId || null;
  }

  const globalVars = globalVariables();
  const [message, setMessage] = useState(null);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  const validateIcomplete = async (attempt) => {
    if (attempt === undefined) {
      attempt = 0;
    }
    attempt++;

    var apiSubscription = await postData(
      `/subscriptions/${subscriptionId}/validate`
    );
    if (attempt < 10 && apiSubscription?.stripe?.status === "incomplete") {
      await delay(attempt <= 2 ? 1000 : 2000);
      apiSubscription = await validateIcomplete(attempt);
    }

    return apiSubscription;
  };

  useEffect(() => {
    const check = async () => {
      try {
        if (subscriptionId) {
          setMessage("Fetching subscription information");
          var apiSubscription = await getData(
            `/subscriptions/${subscriptionId}`
          );

          setMessage("Checking subscription status");
          apiSubscription = await validateIcomplete(0);

          setSubscription(apiSubscription);
          setLoading(false);

          if (
            apiSubscription &&
            apiSubscription?.stripe?.status !== "incomplete"
          ) {
            await refreshTokens();
          }
        }
      } catch (err) {
        postNotification("error", err);
        setLoading(false);
      }
    };
    check();
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      {!loading && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Grid container sx={{ gap: 2 }}>
            <Grid
              item
              xs={12}
              sx={{
                p: 2
              }}
            >
              <Typography variant="h3" gutterBottom>
                Subscription Information
              </Typography>
            </Grid>
            {subscription?.stripe?.status === "incomplete" && (
              <Grid
                item
                xs={12}
                sx={{
                  p: 2
                }}
              >
                <Typography variant="h3" gutterBottom>
                  Error Verifying Subscription
                </Typography>
                <Typography variant="body1" paragraph>
                  It looks like something has gone wrong.
                </Typography>
                <Typography variant="body1" paragraph>
                  Your subscription cannot be verified, please contact{" "}
                  <Link href="/support" underline="none">
                    Support
                  </Link>
                </Typography>
              </Grid>
            )}
            {subscription && subscription?.stripe?.status !== "incomplete" && (
              <Grid
                item
                xs={12}
                sx={{
                  p: 2
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Typography variant="body1" gutterBottom>
                      Product
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {subscription?.stripe?.plan?.name ||
                        globalVars?.pricing?.products?.[
                          subscription.stripe?.priceId
                        ]?.name ||
                        "Subscription"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body1" gutterBottom>
                      Status
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {titleCase(
                        subscription.stripe?.status ||
                          (subscription.active
                            ? subscription.isTrial
                              ? "Trial"
                              : "Active"
                            : "Inactive")
                      )}
                    </Typography>
                  </Grid>

                  {subscription.stripe?.plan && (
                    <>
                      {subscription.stripe.plan.amount !== undefined && (
                        <>
                          <Grid item xs={4}>
                            <Typography variant="body1" gutterBottom>
                              Price
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography
                              variant="body1"
                              gutterBottom
                              sx={{ fontWeight: 600 }}
                            >
                              {(
                                subscription.stripe.plan.currency || "usd"
                              ).toUpperCase()}{" "}
                              {(subscription.stripe.plan.amount / 100).toFixed(
                                2
                              )}
                            </Typography>
                          </Grid>
                        </>
                      )}

                      {subscription.stripe.plan.interval && (
                        <>
                          <Grid item xs={4}>
                            <Typography variant="body1" gutterBottom>
                              Interval
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography
                              variant="body1"
                              gutterBottom
                              sx={{ fontWeight: 600 }}
                            >
                              {subscription.stripe.plan.interval_count
                                ? `${titleCase(
                                    subscription.stripe.plan.interval
                                  )}ly`
                                : `Every ${
                                    subscription.stripe.plan.interval_count
                                  } ${titleCase(
                                    subscription.stripe.plan.interval
                                  )}s`}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </>
                  )}

                  {subscription.isTrial && (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body1" gutterBottom>
                          Trial Expiry
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography
                          variant="body1"
                          gutterBottom
                          sx={{ fontWeight: 600 }}
                        >
                          {moment(subscription.expirationDate).format(
                            "MMMM D, YYYY LT"
                          )}
                        </Typography>
                      </Grid>
                    </>
                  )}

                  {!subscription.stripe?.cancelAtPeriodEnd &&
                    subscription.nextUpdate && (
                      <>
                        <Grid item xs={4}>
                          <Typography variant="body1" gutterBottom>
                            Next Charge
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography
                            variant="body1"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                          >
                            {moment(subscription.nextUpdate).format(
                              "MMMM D, YYYY LT"
                            )}
                          </Typography>
                        </Grid>
                      </>
                    )}

                  {subscription.active &&
                    !subscription.stripe?.cancelAtPeriodEnd && (
                      <>
                        <Grid item xs={12}>
                          <Button
                            color="error"
                            variant="outlined"
                            fullWidth
                            onClick={async () => {
                              try {
                                await confirm({
                                  title: `Are you sure?`,
                                  description: `Are you sure you want to cancel your subscription? You will continue to have access to subsciption features until ${moment(
                                    subscription.nextUpdate
                                  ).format("MMMM D, YYYY LT")}`,
                                  dialogProps: {
                                    sx: {
                                      zIndex: 99999
                                    }
                                  }
                                });
                              } catch (err) {
                                return;
                              }

                              // TODO: implement reasons for ending subscription

                              try {
                                setMessage("Canceling subscription");
                                setLoading(true);
                                var apiSubscription = await postData(
                                  `/subscriptions/${subscriptionId}/cancel`
                                );
                                setSubscription(apiSubscription);
                              } catch (err) {
                                postNotification("error", err);
                              }
                              setLoading(false);
                            }}
                          >
                            Cancel
                          </Button>
                        </Grid>
                      </>
                    )}

                  {subscription.active &&
                    subscription.stripe?.cancelAtPeriodEnd && (
                      <>
                        <Grid item xs={12}>
                          <Button
                            color="success"
                            variant="outlined"
                            fullWidth
                            onClick={async () => {
                              try {
                                setMessage("Un-Canceling subscription");
                                setLoading(true);
                                var apiSubscription = await postData(
                                  `/subscriptions/${subscriptionId}/uncancel`
                                );
                                setSubscription(apiSubscription);
                              } catch (err) {
                                postNotification("error", err);
                              }
                              setLoading(false);
                            }}
                          >
                            Renew
                          </Button>
                        </Grid>
                      </>
                    )}

                  {!subscription.active && (
                    <>
                      <Grid item xs={12}>
                        <Button
                          color="success"
                          variant="outlined"
                          fullWidth
                          href="/subscription"
                        >
                          Renew
                        </Button>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            )}
            {!subscription && !loading && (
              <Grid
                item
                xs={12}
                sx={{
                  p: 2
                }}
              >
                <Typography variant="body1" gutterBottom>
                  No subscription found
                </Typography>
              </Grid>
            )}
          </Grid>
        </Container>
      )}
      {loading && (
        <ModalLoadingMessage
          open
          header="Subscription Confirmation"
          message={message}
        />
      )}
    </Box>
  );
}
